/**
 * Service for formatting expert responses in inquiry conversations.
 * Uses the AI-powered /v2/inquiries/format-response endpoint.
 */

import { FORMAT_RESPONSE } from '@/constants/formatResponse';
import { fetchClient } from '@/util/fetchClient';

/**
 * Result from the format response service.
 */
export interface FormatResponseResult {
  formattedResponse: string;
}

export const FORMAT_RESPONSE_DEFAULT_ERROR = 'Failed to format response. Please try again.';

/**
 * Error codes that can be returned by the format response API.
 */
export const FORMAT_RESPONSE_ERROR_CODES = {
  RATE_LIMITED: 429,
  SERVICE_UNAVAILABLE: 502,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  INTERNAL_ERROR: 500,
  TIMEOUT: 408,
} as const;

/**
 * User-friendly error messages mapped to API status codes.
 */
export const FORMAT_RESPONSE_ERROR_MESSAGES: Record<number, string> = {
  [FORMAT_RESPONSE_ERROR_CODES.RATE_LIMITED]:
    'AI service is busy. Please wait a moment and try again.',
  [FORMAT_RESPONSE_ERROR_CODES.SERVICE_UNAVAILABLE]:
    'AI service temporarily unavailable. Please try again.',
  [FORMAT_RESPONSE_ERROR_CODES.BAD_REQUEST]:
    'Invalid request. Please check your input and try again.',
  [FORMAT_RESPONSE_ERROR_CODES.UNAUTHORIZED]: 'Session expired. Please refresh the page.',
  [FORMAT_RESPONSE_ERROR_CODES.FORBIDDEN]: 'You do not have permission to use this feature.',
  [FORMAT_RESPONSE_ERROR_CODES.INTERNAL_ERROR]: 'An unexpected error occurred. Please try again.',
  [FORMAT_RESPONSE_ERROR_CODES.TIMEOUT]: 'Request timed out. Please try again.',
};

/**
 * Custom error class for format response failures.
 */
export class FormatResponseError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly originalError?: string
  ) {
    super(message);

    this.name = 'FormatResponseError';
  }
}

/**
 * Formats expert response text using AI-powered formatting service.
 *
 * The API accepts a single text field containing the expert response
 * and optional additional instructions. The backend handles conversation
 * context internally via the inquiry association.
 *
 * @param text - The expert response text to format (may include additional instructions)
 * @returns Promise resolving to the formatted response
 * @throws FormatResponseError with user-friendly message and status code
 *
 * @example
 * ```ts
 * const result = await formatInquiryResponse(
 *   "Patient has stage 2 breast cancer...\n\n--- ADDITIONAL INSTRUCTIONS ---\nKeep it brief"
 * );
 * console.log(result.formattedResponse);
 * ```
 */
export async function formatInquiryResponse(text: string): Promise<FormatResponseResult> {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => controller.abort(), FORMAT_RESPONSE.API_TIMEOUT_MS);

  try {
    const { data, error } = await fetchClient.POST('/v2/inquiries/format-response', {
      body: { text },
      signal: controller.signal,
    });

    if (error || !data) {
      const statusCode =
        (error as { status?: number })?.status ?? FORMAT_RESPONSE_ERROR_CODES.INTERNAL_ERROR;

      const userMessage =
        FORMAT_RESPONSE_ERROR_MESSAGES[statusCode] ?? FORMAT_RESPONSE_DEFAULT_ERROR;

      const originalError = (error as { body?: { error?: string } })?.body?.error ?? undefined;

      throw new FormatResponseError(userMessage, statusCode, originalError);
    }

    return { formattedResponse: data.formatted_text };
  } catch (err) {
    if (err instanceof FormatResponseError) {
      throw err;
    }

    if (err instanceof Error && err.name === 'AbortError') {
      throw new FormatResponseError(
        FORMAT_RESPONSE_ERROR_MESSAGES[FORMAT_RESPONSE_ERROR_CODES.TIMEOUT],
        FORMAT_RESPONSE_ERROR_CODES.TIMEOUT
      );
    }

    throw new FormatResponseError(
      FORMAT_RESPONSE_DEFAULT_ERROR,
      FORMAT_RESPONSE_ERROR_CODES.INTERNAL_ERROR,
      err instanceof Error ? err.message : undefined
    );
  } finally {
    clearTimeout(timeoutId);
  }
}
